import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';

/**
 * Minimal wrapper for the custom dropdown menu layout.
 *
 * The standalone dropdown does not use `<header-menu>` / `<overflow-list>`, so the work those
 * did — calling `setHeaderMenuStyle()` to toggle `#header-component[data-menu-style]` between
 * `menu` (desktop) and `drawer` (mobile/touch) — has to happen here. Without it the attribute is
 * never set to `menu`, the base `.header__drawer { display: flex }` wins, and the desktop menu
 * stays hidden (hamburger only).
 *
 * Extends Component (like `<header-menu>`) so that the Section Rendering API / Theme Editor morph
 * calls `updatedCallback()` after a re-render — the morph strips JS-set attributes such as
 * `data-menu-style`, so it must be re-asserted there, not only on `connectedCallback`.
 */
class HeaderDropdown extends Component {
  #onResize = debounce(() => setHeaderMenuStyle(), 100);

  connectedCallback() {
    super.connectedCallback();
    setHeaderMenuStyle();
    window.addEventListener('resize', this.#onResize);
    // `toggle` does not bubble — listen in the capture phase so one handler covers every <details>.
    this.addEventListener('toggle', this.#onToggle, true);
  }

  updatedCallback() {
    super.updatedCallback();
    setHeaderMenuStyle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('toggle', this.#onToggle, true);
  }

  /**
   * Keep the summary's aria-expanded in sync with the native open state.
   * @param {Event} event
   */
  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown);
}
